import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert, Container, Box, Snackbar } from "@mui/material";
import { parseQueryParams, createQueryParams } from "@/tools/tools";
import useStore from "@store";
import MainContainer from "../MainContainer";
import ExternalScriptContainer from "./Externals/ExternalScriptContainer";
import Footer from "../Footer";

interface RouterContentProps {
  customerConfig: any;
}

const RouterContent: React.FC<RouterContentProps> = ({ customerConfig }) => {
  const { searchObject, setSearchObject, error, setError } = useStore();
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Read filter parameter from URL and update search object
  useEffect(() => {
    const queryParams = parseQueryParams(location.search);
    if (queryParams && Object.keys(queryParams).length) {
      setSearchObject(queryParams);
    }
  }, []);

  // Update filter paramter in URL
  useEffect(() => {
    const queryParams = createQueryParams(searchObject);
    navigate({ search: `?${queryParams}` }, { replace: true });
  }, [searchObject]);

  // scroll to top after each route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // hide custom elements after each route change
  useEffect(() => {
    if (location.pathname !== "/") {
      const hideElements = customerConfig?.styles?.hidden;
      hideElements?.forEach((selector: string) => {
        const elements = document.querySelectorAll(selector);
        elements.forEach((element) => {
          (element as HTMLElement).style.display = "none";
        });
      });
    }
  }, [location.pathname]);

  // Snackbar trigger
  useEffect(() => {
    if (error) {
      setOpenSnackbar(true);
    }
  }, [error]);

  // Snackbar close handler
  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
    setError("");
  };

  const sxContainer = customerConfig?.styles?.custom?.sxContainer || {};

  return (
    <Box component="section">
      <Container id="mpi-container" sx={{ padding: { xs: 0 }, textAlign: "left", ...sxContainer }}>
        {error && (
          <Snackbar
            open={openSnackbar}
            autoHideDuration={5000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            sx={{
              position: "relative",
              top: 30,
              left: "50%",
              marginBottom: 60,
              transform: "translateX(-50%)"
            }}
          >
            <Alert onClose={() => setOpenSnackbar(false)} severity="error">
              {error}
            </Alert>
          </Snackbar>
        )}
        <MainContainer customerConfig={customerConfig} />
        <ExternalScriptContainer customerConfig={customerConfig} />
        <Footer />
      </Container>
    </Box>
  );
};

export default RouterContent;
